/**
 * IflowDiagram: coordinate-based iFlow renderer (1:1 with the SAP Web GUI).
 * Uses the BPMNShape/BPMNEdge coordinates stored in the BPMN, rendered in this
 * product's iio design language.
 * Parser/icons/categories come from utils/bpmnShared (a single source for every renderer).
 *
 * Optional props (the component renders in its plain default state without them):
 * - selectedId: highlights one element persistently (detail panels).
 * - marks: { [elementId]: "add" | "del" | "chg" }, diff decoration without a glow:
 *   a 2px border, a corner badge (+/-/delta), and the label prefix from MARK[key].prefix.
 *   Removed elements are dashed and at 60% opacity. Color never carries the meaning alone.
 * - onlyChanges: dims unmarked elements (the "changes only" filter).
 */
import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { parseBpmnElements, parseBpmnLayout, clipEdge as clip, iconFor, TYPE_LABEL, categoryOf, layoutNodeLabel } from "../utils/bpmnShared";

const MARK = {
  add: { color: "var(--iio-success)", badge: "+", prefix: "NEU" },
  del: { color: "var(--iio-critical)", badge: "−", prefix: "WEG" },
  chg: { color: "var(--iio-warning)", badge: "Δ", prefix: "GEÄND." },
};

const MIN_SCALE = 0.2, MAX_SCALE = 4;
const ZBTN = { background: "none", border: 0, cursor: "pointer", color: "var(--iio-text-2)", fontSize: 14, fontWeight: 600, height: 20, width: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", borderRadius: 4 };

export default function IflowDiagram({ bpmnXml, onElementClick, height, selectedId, marks, onlyChanges }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [vt, setVt] = useState({ x: 0, y: 0, scale: 1 });
  const [panning, setPanning] = useState(false);
  const drag = useRef({ on: false });
  const svgRef = useRef(null);
  const layout = useMemo(() => parseBpmnLayout(bpmnXml), [bpmnXml]);
  const elements = useMemo(() => parseBpmnElements(bpmnXml), [bpmnXml]);
  const { shapes, edges, bbox } = layout;

  // The viewBox fits the whole drawing onto the screen; this fit factor has to be
  // known, otherwise the percentage display lies (observed: it showed "100%" while
  // actually at 29%) and the level of detail cannot track the real scale.
  const vbW = bbox.w + 100, vbH = bbox.h + 100;
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = svgRef.current; if (!el) return;
    const read = () => { const r = el.getBoundingClientRect(); setBox({ w: r.width, h: r.height }); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fitScale = box.w && box.h ? Math.min(box.w / vbW, box.h / vbH) : 1;
  const eff = fitScale * vt.scale;            // actual on-screen scale
  const zoomLevel = Math.round(eff * 100);

  // Screen point -> viewBox coordinates (accounts for preserveAspectRatio automatically)
  const toUser = (clientX, clientY) => {
    const svg = svgRef.current; if (!svg || !svg.getScreenCTM) return null;
    const m = svg.getScreenCTM(); if (!m) return null;
    const pt = svg.createSVGPoint(); pt.x = clientX; pt.y = clientY;
    return pt.matrixTransform(m.inverse());
  };
  // 1:1 has to stay reachable even for large flows (fit-to-screen can be as low as 0.15 there)
  const maxScale = Math.max(MAX_SCALE, fitScale ? 1.2 / fitScale : MAX_SCALE);
  const clampScale = s => Math.max(MIN_SCALE, Math.min(maxScale, s));
  // Zoom toward the cursor: the point under the pointer stays fixed
  const zoomAt = (next, clientX, clientY) => {
    const u = clientX == null ? null : toUser(clientX, clientY);
    setVt(p => {
      const s = clampScale(next(p.scale));
      if (s === p.scale) return p;
      if (!u) return { ...p, scale: s };
      const k = s / p.scale;
      return { x: u.x - (u.x - p.x) * k, y: u.y - (u.y - p.y) * k, scale: s };
    });
  };
  const zoom = d => zoomAt(s => s * (d > 0 ? 1.25 : 1 / 1.25), null, null);
  const fit = () => setVt({ x: 0, y: 0, scale: 1 });
  const oneToOne = () => setVt({ x: 0, y: 0, scale: fitScale ? 1 / fitScale : 1 });

  // Wheel is attached deliberately via addEventListener with passive:false: React
  // registers onWheel as passive, where preventDefault() has no effect and the page
  // scrolls along with it.
  // Gestures: scroll/pinch zooms (toward the cursor), drag pans.
  // Step size is proportional to deltaY and clamped per event: one mouse wheel notch
  // (~100) gives about 11%, the many small trackpad deltas about 1.5% each, so the
  // gesture does not run away (before: 10 trackpad events = 100% -> 20%).
  useEffect(() => {
    const el = svgRef.current; if (!el) return;
    const h = (e) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1;
      const dy = (e.deltaY || 0) * unit;
      if (!dy) return;
      const f = Math.min(1.25, Math.max(0.8, Math.exp(-dy * (Math.abs(dy) < 40 ? 0.0075 : 0.00113))));
      zoomAt(s => s * f, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, [fitScale, vt.scale]);
  const onDown = useCallback((e) => {
    if (e.button !== 0) return;
    window.getSelection && window.getSelection().removeAllRanges();
    drag.current = { on: true, sx: e.clientX, sy: e.clientY, tx: vt.x, ty: vt.y, moved: false };
    setPanning(true);
    const svg = svgRef.current;
    const perPx = (() => { const m = svg && svg.getScreenCTM && svg.getScreenCTM(); return m ? 1 / (m.a || 1) : 1; })();
    const mv = ev => {
      if (!drag.current.on) return;
      const dx = ev.clientX - drag.current.sx, dy = ev.clientY - drag.current.sy;
      if (!drag.current.moved && Math.abs(dx) + Math.abs(dy) < 3) return;
      drag.current.moved = true;
      setVt(p => ({ ...p, x: drag.current.tx + dx * perPx, y: drag.current.ty + dy * perPx }));
    };
    const up = () => { drag.current.on = false; setPanning(false); window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  }, [vt.x, vt.y]);

  const isGateway = t => t && /gateway/i.test(t);
  const isEvent = t => t && /event/i.test(t);
  const poolIds = [], subIds = [], normalIds = [];
  for (const id of Object.keys(shapes)) { const t = elements[id]?.type || ""; if (t === "participant") poolIds.push(id); else if (t === "subProcess") subIds.push(id); else normalIds.push(id); }

  // Only a real click opens the step. With around 60 nodes, panning almost always
  // starts on top of a card; without a threshold every pan would open the
  // detail panel.
  const click = (id, e) => {
    e.stopPropagation();
    if (drag.current.moved) return;
    const info = elements[id] || {};
    onElementClick && onElementClick({ id, name: info.name || id, type: info.type, activityType: info.activityType, properties: info.properties || {} });
  };

  // Diff decoration: SVG border and corner badge around a shape (for events/gateways)
  const mk = (id) => (marks && MARK[marks[id]]) || null;
  // Pools/subprocesses stay fully visible as structural context
  const dimmed = (id) => { if (!onlyChanges || !marks || marks[id]) return false; const ty = elements[id]?.type; return ty !== "participant" && ty !== "subProcess"; };
  const svgMark = (m, sh) => !m ? null : <g pointerEvents="none">
    <rect x={sh.x - 4} y={sh.y - 4} width={sh.w + 8} height={sh.h + 8} rx={8} fill="none" stroke={m.color} strokeWidth={2} strokeDasharray={m.badge === "−" ? "5,4" : undefined} vectorEffect="non-scaling-stroke" />
    <circle cx={sh.x + sh.w + 4} cy={sh.y - 4} r={8} fill={m.color} />
    <text x={sh.x + sh.w + 4} y={sh.y - 3.4} textAnchor="middle" dominantBaseline="middle" fontSize={11} fontWeight={700} fill="var(--iio-bg-2)" fontFamily="Source Sans 3,sans-serif">{m.badge}</text>
  </g>;

  const renderShape = (id) => {
    const sh = shapes[id]; if (!sh) return null;
    const info = elements[id] || {}; const t = info.type || "task"; const name = info.name || ""; const hov = hoveredId === id || selectedId === id;
    const m = mk(id);
    const wrapG = (inner) => <g key={id} opacity={m && m.badge === "−" ? 0.6 : dimmed(id) ? 0.28 : 1}>{inner}{svgMark(m, sh)}</g>;

    if (isGateway(t)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2, g = r * 0.4;
      const sym = t === "parallelGateway"
        ? <g stroke="var(--iio-signal)" strokeWidth={2} strokeLinecap="round"><line x1={cx} y1={cy - g} x2={cx} y2={cy + g} /><line x1={cx - g} y1={cy} x2={cx + g} y2={cy} /></g>
        : <g stroke="var(--iio-signal)" strokeWidth={2} strokeLinecap="round"><line x1={cx - g * .7} y1={cy - g * .7} x2={cx + g * .7} y2={cy + g * .7} /><line x1={cx + g * .7} y1={cy - g * .7} x2={cx - g * .7} y2={cy + g * .7} /></g>;
      return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
        <polygon points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} fill="var(--iio-dia-node)" stroke="var(--iio-signal)" strokeWidth={hov ? 2 : 1.3} vectorEffect="non-scaling-stroke" />{sym}
      </g>);
    }
    if (isEvent(t)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2;
      const cmd = (info.properties?.cmdVariantUri || "").toLowerCase(), nm = name.toLowerCase();
      const isErr = cmd.includes("error") || nm.includes("error"), isTmr = cmd.includes("timer") || nm.includes("timer");
      const isEnd = t === "endEvent";
      const col = isErr ? "var(--iio-critical)" : isEnd ? "var(--iio-critical)" : "var(--iio-success)";
      return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
        <circle cx={cx} cy={cy} r={r} fill="var(--iio-dia-node)" stroke={col} strokeWidth={isEnd ? 2.6 : hov ? 2.2 : 1.6} vectorEffect="non-scaling-stroke" />
        <g stroke={col} fill="none" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round" transform={`translate(${cx - r * .5},${cy - r * .5}) scale(${r / 24})`}>
          <g dangerouslySetInnerHTML={{ __html: isTmr ? '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>' : isErr ? '<path d="M10 4 2 19h20L14 4z"/><path d="M12 10v4"/>' : '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>' }} />
        </g>
        {name && <text x={cx} y={cy + r + 11} textAnchor="middle" fontSize={9} fill="var(--iio-dia-label)" fontFamily="Source Sans 3,sans-serif">{name.length > 22 ? name.slice(0, 20) + "…" : name}</text>}
      </g>);
    }
    if (t === "participant") {
      const iflType = info.properties?.["ifl:type"] || "";
      const isProc = name === "Integration Process" || iflType === "IntegrationProcess";
      const isRecv = iflType === "EndpointRecevier" || iflType === "EndpointReceiver";
      // Solid pool area: only this makes the cards sit on a surface instead of
      // directly on the near-black canvas.
      const col = isRecv ? "var(--iio-signal)" : "var(--iio-dia-pool-border)";
      const lblCol = isRecv ? "var(--iio-signal)" : "var(--iio-dia-pool-label)";
      return wrapG(<g>
        <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} fill="var(--iio-dia-pool)" stroke={col} strokeWidth={1.4} strokeDasharray={isProc ? undefined : "6,4"} rx={6} vectorEffect="non-scaling-stroke" />
        {name && <text x={sh.x + 13} y={sh.y + sh.h / 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={700} letterSpacing="0.08em" fill={lblCol} fontFamily="Source Sans 3,sans-serif" transform={`rotate(-90 ${sh.x + 13} ${sh.y + sh.h / 2})`}>{(name.length > 28 ? name.slice(0, 26) + "…" : name).toUpperCase()}</text>}
      </g>);
    }
    if (t === "subProcess") {
      return wrapG(<g>
        <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} rx={8} fill="transparent" stroke="var(--iio-critical)" strokeWidth={1.2} strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
        {name && <text x={sh.x + 10} y={sh.y + 15} fontSize={10} fontWeight={700} fill="var(--iio-critical)" fontFamily="Source Sans 3,sans-serif">{name}</text>}
      </g>);
    }
    // Task / callActivity / serviceTask card (same design as before, placed at BPMN coordinates)
    // Plus a 3px category accent on the left (Transform/Route/Call/Persist), meaning coded three ways
    const at = info.activityType || "";
    const cat = categoryOf(info);
    const typeLbl = TYPE_LABEL[at] || at || t;
    // SAP shapes are mostly only 100x60px, hence a compact inner layout
    // (icon + type in the header row, name below spanning the full width).
    // The builder layout (icon next to the name) needs at least 130x76px.
    // The card is pure SVG (no foreignObject): scaled HTML content has to be
    // rerasterized by Chrome on every frame; with 28 cards at zoom 3.5 that froze
    // the tab while panning. The label layout comes from bpmnShared, so the doc
    // renderer and the canvas share the same source.
    const L = layoutNodeLabel(name, typeLbl, sh, { eff, minPx: 7 });
    const accent = cat ? cat.color : "var(--iio-dia-label)";
    return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
      <title>{name ? (typeLbl ? `${name} · ${typeLbl}` : name) : typeLbl}</title>
      <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} rx={6} fill="var(--iio-dia-node)"
        stroke={hov ? "var(--iio-brand-fg)" : "var(--iio-dia-node-border)"} strokeWidth={hov ? 2 : 1} vectorEffect="non-scaling-stroke" />
      {cat && <line x1={sh.x + 2.5} y1={sh.y + 7} x2={sh.x + 2.5} y2={sh.y + sh.h - 7} stroke={cat.color} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
      {L.showIcon && <g stroke={accent} fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"
        transform={`translate(${sh.x + 8},${sh.y + 7}) scale(0.46)`} dangerouslySetInnerHTML={{ __html: iconFor(at) }} />}
      {/* Always draw the label, even zoomed far out: empty boxes read like a bug.
          At small scale the font size is raised against the zoom, but only as far
          as it still fits the box (see layoutNodeLabel: more lines means a smaller
          font, capped at 3 lines). */}
      {name && L.lines.map((ln, i) => (
        <text key={i} x={L.cx} y={L.y0 + i * L.lh} textAnchor="middle" dominantBaseline="middle"
          fontSize={L.fs} fontWeight={600} fill="var(--iio-text-1)" fontFamily="Source Sans 3,sans-serif">{ln}</text>
      ))}
      <text x={L.cx} y={L.typeY} textAnchor="middle" dominantBaseline="middle" fontSize={L.tfs} fontWeight={600}
        letterSpacing={L.tls} fill="var(--iio-dia-label)" fontFamily="Source Sans 3,sans-serif">
        {m && sh.w >= 140 ? `${m.prefix} · ${L.typeText}` : L.typeText}</text>
    </g>);
  };

  return (
    <div style={{
      position: "relative", width: "100%", height: height || "100%", overflow: "hidden",
      background: "var(--iio-dia-canvas)",
      backgroundImage: "linear-gradient(to right, var(--iio-dia-grid) 1px, transparent 1px), linear-gradient(to bottom, var(--iio-dia-grid) 1px, transparent 1px)",
      backgroundSize: "24px 24px",
    }}>
      {/* Ship the card styles locally, so the diagram works on every page
          (content, audit, compare, builder) without page-level CSS.
          Colors come from the --iio-dia-*-tokens: the plain bg steps alone do not
          separate a dense graph (bg-2 on bg-0 is only a 1.26:1 contrast in dark mode). */}
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10, display: "flex", alignItems: "center", gap: 2, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: "3px 5px" }}>
        <button onClick={() => zoom(-1)} title="Verkleinern" aria-label="Verkleinern" style={ZBTN}>−</button>
        {/* The display is the actual scale (fit factor times zoom), not relative to the fit */}
        <span title="Tatsächliche Darstellungsgröße" style={{ fontSize: 10.5, color: "var(--iio-text-1)", minWidth: 36, textAlign: "center", fontWeight: 600 }}>{zoomLevel} %</span>
        <button onClick={() => zoom(1)} title="Vergrößern" aria-label="Vergrößern" style={ZBTN}>+</button>
        <span style={{ width: 1, height: 16, background: "var(--iio-border)", margin: "0 3px" }} />
        <button onClick={fit} title="Einpassen" aria-label="Einpassen" style={{ ...ZBTN, fontSize: 11, fontWeight: 600, width: "auto", padding: "0 6px" }}>Einpassen</button>
        <button onClick={oneToOne} title="Originalgröße" aria-label="Originalgröße" style={{ ...ZBTN, fontSize: 11, fontWeight: 600, width: "auto", padding: "0 6px" }}>1:1</button>
      </div>
      <svg ref={svgRef} width="100%" height="100%" style={{ display: "block", cursor: panning ? "grabbing" : "grab", userSelect: "none", WebkitUserSelect: "none" }}
        viewBox={`${bbox.minX - 50} ${bbox.minY - 50} ${bbox.w + 100} ${bbox.h + 100}`} onMouseDown={onDown} onDragStart={e => e.preventDefault()}>
        <defs>
          <marker id="aibArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth={7} markerHeight={7} orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="var(--iio-dia-edge)" /></marker>
          <marker id="aibArrowMsg" viewBox="0 0 10 10" refX="9" refY="5" markerWidth={7} markerHeight={7} orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="var(--iio-signal)" /></marker>
        </defs>
        <g transform={`translate(${vt.x},${vt.y}) scale(${vt.scale})`}>
          {poolIds.map(renderShape)}
          {subIds.map(renderShape)}
          {edges.map(e => {
            const pts = clip(e, shapes, elements).map(w => `${w.x},${w.y}`).join(" ");
            const em = mk(e.id);
            return <polyline key={e.id} points={pts} fill="none" opacity={em && em.badge === "−" ? 0.6 : dimmed(e.id) ? 0.28 : 1} stroke={em ? em.color : e.isMessageFlow ? "var(--iio-signal)" : "var(--iio-dia-edge)"} strokeWidth={em ? 2 : e.isMessageFlow ? 1.1 : 1.4} strokeDasharray={e.isMessageFlow || (em && em.badge === "−") ? "5,4" : undefined} markerEnd={e.isMessageFlow ? "url(#aibArrowMsg)" : "url(#aibArrow)"} />;
          })}
          {normalIds.map(renderShape)}
        </g>
      </svg>
    </div>
  );
}
